import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReplayIcon from "@mui/icons-material/Replay";
import { round } from "lodash";
import { Grid, Stack, Typography, StatusExpandContent, Button } from "@financeable-com-au/financeable-ui";
import { getIsCommercialOrBusinessApplication, REFERRAL_FEE } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  type Fee,
  FeeFrequency,
  InArrears,
  ProductsQuote,
  RepaymentFrequency,
} from "@/store/slices/types/applicationFormSlice";
import { setQuoteCalculator } from "@/store/slices/quoteCalculator";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { formatFeesForSaveQuote, getSelectableFeesList } from "@/utils/lenderUtils";
import { FeeFields } from "./FeeFields";
import { LoanFields } from "./LoanFields";

interface QuoteCalculatorExpandedProps {
  productsQuote: ProductsQuote | null;
}

const QuoteCalculatorExpanded = ({ productsQuote }: QuoteCalculatorExpandedProps) => {
  const { showQuoteCalculator, product, lenderCard, lenderCardCustom, quoteCalculatorFormik, inArrears, setInArrears } =
    useLenderCardContext();
  const { lender, productType } = product ?? {};
  const { applicationType } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);
  const dispatch = useDispatch();
  const defaultInArrearsValue = InArrears.Yes;
  const [pendingQuoteSource, setPendingQuoteSource] = useState<"lenderDefault" | "savedQuote" | null>(null);

  const saveQuote = ({ actionFor, value }: { actionFor: string; value?: string | Fee[] | FeeFrequency }) => {
    const defaultFields = {
      lender,
      productType,
      loanAmount: round(lenderCardCustom.loanAmount.value ?? 0, 2),
      repayments: round(lenderCardCustom.repayments.monthly.value ?? 0, 2),
      rate: round(lenderCardCustom.rate.default.value ?? 0, 2),
      brokerage: isCommercialOrBusinessApplication
        ? round(lenderCardCustom.brokerage.percentage.value ?? 0, 2)
        : undefined,
      brokerageAmount: isCommercialOrBusinessApplication
        ? round(lenderCardCustom.brokerage.amount.value ?? 0, 2)
        : undefined,
      referrer: lenderCardCustom.referrer,
    };
    if (quoteCalculatorFormik?.isValid) {
      switch (actionFor) {
        case "rate":
          dispatch(setQuoteCalculator({ ...defaultFields, rate: round(lenderCardCustom.rate.default.value ?? 0, 2) }));
          break;
        case "brokerage":
          dispatch(
            setQuoteCalculator({
              ...defaultFields,
              brokerage: round(lenderCardCustom.brokerage.percentage.value ?? 0, 2),
              brokerageAmount: round(lenderCardCustom.brokerage.amount.value ?? 0, 2),
            }),
          );
          break;
        case "inArrears":
          dispatch(setQuoteCalculator({ ...defaultFields, inArrears: value as InArrears }));
          break;
        case "fees":
          dispatch(
            setQuoteCalculator({
              ...defaultFields,
              fees: formatFeesForSaveQuote(lenderCardCustom.fees.application),
            }),
          );
          break;
        case "useLenderDefault":
          dispatch(
            setQuoteCalculator({
              ...defaultFields,
              loanAmount: round(lenderCard.loanAmount.value ?? 0, 2),
              rate: round(lenderCard.rate.default.value ?? 0, 2),
              brokerage: isCommercialOrBusinessApplication
                ? round(lenderCard.brokerage.percentage.value ?? 0, 2)
                : undefined,
              brokerageAmount: isCommercialOrBusinessApplication
                ? round(lenderCard.brokerage.amount.value ?? 0, 2)
                : undefined,
              inArrears: defaultInArrearsValue,
              fees: formatFeesForSaveQuote(lenderCard.fees.application),
              repayments: round(lenderCard.repayments.monthly.value ?? 0, 2),
            }),
          );
          break;
        case "useSavedQuote":
          dispatch(
            setQuoteCalculator({
              ...defaultFields,
              rate: round(lenderCardCustom.rate.default.value ?? 0, 2),
              brokerage: isCommercialOrBusinessApplication
                ? round(lenderCardCustom.brokerage.percentage.value ?? 0, 2)
                : undefined,
              brokerageAmount: isCommercialOrBusinessApplication
                ? round(lenderCardCustom.brokerage.amount.value ?? 0, 2)
                : undefined,
              inArrears: value as InArrears,
              fees: formatFeesForSaveQuote(lenderCardCustom.fees.application),
            }),
          );
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    if (!pendingQuoteSource) return;

    saveQuote({
      actionFor: pendingQuoteSource === "lenderDefault" ? "useLenderDefault" : "useSavedQuote",
      value: inArrears,
    });
    setPendingQuoteSource(null);
  }, [pendingQuoteSource]);

  const handleUseLenderDefault = () => {
    setInArrears(defaultInArrearsValue);
    if (quoteCalculatorFormik) {
      // Reset rather than set, so the lender's defaults become the starting point that later edits are compared to.
      quoteCalculatorFormik.resetForm({
        values: {
          rate: round(lenderCard.rate.default.value ?? 0, 2),
          brokerage: round(lenderCard.brokerage.percentage.value ?? 0, 2),
          inArrears: defaultInArrearsValue,
          repaymentFrequency: RepaymentFrequency.Monthly,
          fees: lenderCard.fees.application,
          selectFee: "",
          selectableFeesList: getSelectableFeesList(lenderCard.fees.application),
          referrer: "",
          lenderDefaultFees: true,
        },
      });
    }
    setPendingQuoteSource("lenderDefault");
  };

  const handleUseSavedQuote = () => {
    if (quoteCalculatorFormik) {
      const { quote } = productsQuote ?? {};
      const savedInArrears = (quote?.inArrears as InArrears) ?? defaultInArrearsValue;
      setInArrears(savedInArrears);
      const referralFeeFromQuote = (quote?.fees || []).find(
        (fee: Fee) => fee.key === REFERRAL_FEE || fee.name?.toLowerCase().includes("referral"),
      );
      quoteCalculatorFormik.setValues({
        rate: round(quote?.rate ?? 0, 2),
        brokerage: round(quote?.brokerage ?? 0, 2),
        inArrears: savedInArrears,
        repaymentFrequency: (quote?.repaymentFrequency as RepaymentFrequency) ?? RepaymentFrequency.Monthly,
        fees: quote?.fees ?? [],
        selectFee: "",
        selectableFeesList: getSelectableFeesList(quote?.fees ?? []),
        referrer: referralFeeFromQuote?.referrer ?? "",
        lenderDefaultFees: false,
      });
      setPendingQuoteSource("savedQuote");
    }
  };

  return (
    <StatusExpandContent expanded={showQuoteCalculator}>
      <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography level="title-md" component="h5" mb={3}>
          Quote Calculator
        </Typography>
        <Stack flexDirection="row" columnGap={2}>
          {!!productsQuote && (
            <Button
              variant="secondary"
              intent="neutral"
              size="sm"
              endDecorator={<ReplayIcon sx={{ fontSize: "1rem" }} />}
              onClick={() => {
                handleUseSavedQuote();
              }}
            >
              Use Saved Quote
            </Button>
          )}
          <Button
            variant="secondary"
            intent="neutral"
            size="sm"
            endDecorator={<ReplayIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => {
              handleUseLenderDefault();
            }}
          >
            Use Lender Default
          </Button>
        </Stack>
      </Stack>
      <form onSubmit={quoteCalculatorFormik?.handleSubmit}>
        <Grid container columnSpacing={{ xs: 0, sm: 12 }} rowSpacing={{ xs: 4, sm: 0 }}>
          <Grid xs={12} sm={6}>
            <LoanFields saveQuote={saveQuote} />
          </Grid>
          <Grid xs={12} sm={6}>
            <FeeFields saveQuote={saveQuote} />
          </Grid>
        </Grid>
      </form>
    </StatusExpandContent>
  );
};

export { QuoteCalculatorExpanded };
